import { useCallback, useRef } from "react";
import { waitForVoiceCliSwitch } from "@/desktop/voice-cli-switch";
import { parseNewChatCommand } from "@/lib/new-chat-command";
import { useInputBarTarget } from "@/stores/input-bar-target-store";
import type { CursorInputContextValue } from "@/components/layout/cursor-input-context";
import type { InputAgent } from "@/components/panels/agent-stack";
import type { TaskChatKind } from "@/lib/task-chat-kind";
import {
  currentFrameCenter,
  resolveConversationFrameTargetAgent,
} from "./conversation-frame-utils";

export function useConversationFrameSubmit({
  agentKey,
  taskChat = null,
  ctx,
  draft,
  executionId,
  runtimeSessionId,
  frameAgent,
  frameId,
  conversationFrameId,
  conversationId,
  startNewConversation,
  projectAgentId,
  resetSelectedAgent,
  selectedAgentIds,
  createConversation,
}: {
  taskChat?: TaskChatKind | null;
  ctx: CursorInputContextValue | null;
  draft: string;
  frameId: string;
  conversationFrameId?: string;
  conversationId?: string;
  startNewConversation: boolean;
  agentKey?: string;
  projectAgentId?: string;
  executionId?: string;
  runtimeSessionId?: string;
  frameAgent: InputAgent | null;
  selectedAgentIds: Set<string>;
  resetSelectedAgent: () => void;
  createConversation?: () => Promise<string | undefined>;
}) {
  const composerId = `conversation-frame:${conversationFrameId ?? frameId}`;
  const newChatRef = useRef<{ draft: string; conversationId: string } | null>(
    null,
  );
  return useCallback(async () => {
    let text = draft.trim();
    if (taskChat) {
      if (!ctx || !conversationId) return false;
      const hasImageAttachment = (ctx.attachedImages.length ?? 0) > 0;
      if (!text && !hasImageAttachment) return false;
      return ctx.onSubmit(text, "denker", {
        conversationId,
        conversationFrameId,
        composerId,
        nativeChildInstruction: true,
        startNewConversation: false,
        ignoreInputBarSessionState: true,
        textOnly: true,
        allowImages: true,
      });
    }
    const hasImageAttachment = (ctx?.attachedImages.length ?? 0) > 0;
    if (!ctx || (!text && !hasImageAttachment)) return;

    await waitForVoiceCliSwitch();
    const newChatRequest = parseNewChatCommand(text);
    let createdConversationId: string | undefined;
    if (createConversation && newChatRequest !== null) {
      text = newChatRequest;
      createdConversationId =
        newChatRef.current?.draft === draft
          ? newChatRef.current.conversationId
          : await createConversation();
      if (!createdConversationId) return false;
      newChatRef.current = { draft, conversationId: createdConversationId };
      if (!text && !hasImageAttachment) {
        newChatRef.current = null;
        return true;
      }
    }
    const targetAgent = resolveConversationFrameTargetAgent({
      text,
      agents: ctx.inputAgents,
      selectedAgentIds,
      frameAgent,
    });
    const targetAgentKey = "denker";
    const targetIsFrameAgent =
      targetAgent && projectAgentId
        ? targetAgent.id === projectAgentId
        : agentKey
          ? agentKey.toLowerCase() === targetAgentKey
          : frameAgent?.name.toLowerCase() === targetAgentKey;
    const route = useInputBarTarget.getState();
    const useVoiceRoute =
      route.startNewConversation &&
      route.newConversationSource === "voice" &&
      route.newConversationFrameId === (conversationFrameId ?? frameId) &&
      (!route.newConversationAgentId ||
        route.newConversationAgentId === targetAgent?.id);
    const startNew =
      !createdConversationId && (startNewConversation || useVoiceRoute);
    const framePosition = currentFrameCenter(frameId);

    if (!targetIsFrameAgent && !createdConversationId) {
      const sent = await ctx.onSubmit(text, targetAgentKey, {
        startNewConversation: startNew,
        composerId,
        ignoreInputBarSessionState: !useVoiceRoute,
        position: framePosition,
      });
      if (sent === false) return false;
      resetSelectedAgent();
    } else {
      // A frame owns the durable Conversation, not the runtime snapshot from
      // when it opened. Omitting that supplemental id lets the backend refresh
      // this Conversation's exact foreground or crash-resumable segment.
      const sent = await ctx.onSubmit(text, targetAgentKey, {
        conversationFrameId,
        composerId,
        conversationId:
          createdConversationId ?? (startNew ? undefined : conversationId),
        executionId:
          startNew || createdConversationId ? undefined : executionId,
        ...(!startNew && !createdConversationId && runtimeSessionId
          ? { runtimeSessionId }
          : {}),
        startNewConversation: startNew,
        ignoreInputBarSessionState:
          Boolean(createdConversationId) || !useVoiceRoute,
        position: framePosition,
      });
      if (sent === false) return false;
    }
    newChatRef.current = null;
    return true;
  }, [
    taskChat,
    agentKey,
    ctx,
    draft,
    executionId,
    runtimeSessionId,
    frameAgent,
    frameId,
    composerId,
    conversationFrameId,
    conversationId,
    startNewConversation,
    projectAgentId,
    resetSelectedAgent,
    selectedAgentIds,
    createConversation,
  ]);
}
